import React, { Context } from 'react';
import TablesContext, { TableConfig, ITableContext } from './table-context';
import { ColumnProps } from '../column';
import { HocWrapper } from './../../../utils/hoc-wrapper';
const LS_TABLE_CONTEXT = 'table-context';

export default function tableController<P>(
  Wrapped: HocWrapper<P>,
): React.ComponentClass<P> {
  class WrappedComponent extends React.Component<P, ITableContext> {
    constructor(props: P) {
      super(props);

      const LSTableContextString = localStorage.getItem(LS_TABLE_CONTEXT);
      const LSTableContext = JSON.parse(LSTableContextString);
      this.state = {
        tables: LSTableContext || {},
      };
    }

    setTableState = (tableID: string, newState: Partial<TableConfig>) => {
      const { tables } = this.state;
      this.setState({
        tables: {
          ...tables,
          [tableID]: {
            ...tables[tableID],
            ...newState,
          },
        },
      });
    };

    getTableContext = (tableID: string) =>
      this.state.tables[tableID] || ({} as TableConfig);

    toggleSelect = (tableID: string) => (rowID: string) => {
      const { selectedRows } = this.getTableContext(tableID);
      if (selectedRows.has(rowID)) {
        selectedRows.delete(rowID);
      } else {
        selectedRows.add(rowID);
      }
      this.setTableState(tableID, { selectedRows: new Set(selectedRows) });
    };

    onSortChange = (tableID: string) => (
      sortKey: string,
      sortAscending: boolean,
    ) => {
      this.setTableState(tableID, { sortKey, sortAscending });
    };

    onSearchChange = (tableID: string) => (search: string) => {
      this.setTableState(tableID, { search });
    };

    selectAll = (tableID: string) => (rowIDs: string[]) => {
      this.setTableState(tableID, { selectedRows: new Set(rowIDs) });
    };

    deselectAll = (tableID: string) => () => {
      this.setTableState(tableID, { selectedRows: new Set() });
    };

    toggleColumn = (tableID: string) => (columnKey: string) => {
      const { visibleColumns } = this.getTableContext(tableID);
      const nextVisibleColumns = { ...visibleColumns };
      nextVisibleColumns[columnKey] = !nextVisibleColumns[columnKey];
      this.setTableState(tableID, { visibleColumns: nextVisibleColumns });
    };

    toggleColumns = (tableID: string) => (columnKeys: string[]) => {
      const { visibleColumns } = this.getTableContext(tableID);
      const nextVisibleColumns = { ...visibleColumns };
      columnKeys.forEach(columnKey => {
        nextVisibleColumns[columnKey] = !nextVisibleColumns[columnKey];
      });
      this.setTableState(tableID, { visibleColumns: nextVisibleColumns });
    };

    setInitialConfig = (tableID: string) => (config: TableConfig) => {
      const { columns, search, sortKey, sortAscending } = config;
      const { visibleColumns } = this.getTableContext(tableID);

      const previousVisibleColumns = visibleColumns || {};

      const nextVisibleColumns = columns.reduce(
        (obj, col) => ({
          ...obj,
          [col.dataKey]:
            previousVisibleColumns[col.dataKey] || !col.defaultHidden,
        }),
        {},
      );

      this.setTableState(tableID, {
        columns,
        search,
        sortKey,
        sortAscending,
        selectedRows: new Set(),
        visibleColumns: nextVisibleColumns,
      });
    };

    componentDidUpdate() {
      const { tables } = this.state;
      const persistantConfig = Object.keys(tables).reduce((res, key) => {
        const { visibleColumns } = tables[key];
        return { ...res, [key]: { visibleColumns } };
      }, {});

      localStorage.setItem(LS_TABLE_CONTEXT, JSON.stringify(persistantConfig));
    }

    render() {
      const provide = {
        ...this.state,
        toggleColumn: this.toggleColumn,
        toggleColumns: this.toggleColumns,
        toggleSelect: this.toggleSelect,
        selectAll: this.selectAll,
        onSortChange: this.onSortChange,
        onSearchChange: this.onSearchChange,
        deselectAll: this.deselectAll,
        setInitialConfig: this.setInitialConfig,
      };

      return (
        <TablesContext.Provider value={provide}>
          <Wrapped {...this.props} />
        </TablesContext.Provider>
      );
    }
  }
  return WrappedComponent;
}
